import { useEffect, useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { announcementDismissKey } from './keys';

export interface AnnouncementModalProps {
  /**
   * Stable announcement id. Drives the localStorage dismiss key
   * `hmp:announcement:<id>`. Changing it re-shows the announcement to everyone,
   * so bump a version suffix (e.g. `:v2`) when the copy materially changes.
   */
  id: string;
  /**
   * Audience gate. The modal shows only when `eligible` is true AND the
   * one-time dismiss flag is unset. A user the announcement does not apply to
   * never sees it and never burns the flag.
   */
  eligible: boolean;
  title: string;
  /** Short summary line under the title. */
  description: ReactNode;
  /** The announcement body. */
  body: ReactNode;
  /** Primary CTA label (Title Case). Clicking it dismisses. */
  primaryLabel: string;
  /** Optional extra work to run on the primary click, after the dismiss. */
  onPrimary?: () => void;
  /** Optional secondary CTA. Clicking it dismisses, then runs onClick. */
  secondary?: { label: string; onClick: () => void };
}

/**
 * Generic, reusable one-time announcement modal.
 *
 * Owns the one-time-via-localStorage behavior generically: presence of the
 * `hmp:announcement:<id>` key (set on any dismiss path) means this announcement
 * is never shown again. This is an announcement, not security state — no DB
 * column, no backend endpoint (project HARD rule against pre-production
 * legacy/flag shims).
 *
 * Feature-specific announcements are entries in the registry
 * (`announcements.tsx`); DashboardLayout renders the first eligible,
 * not-yet-dismissed one through this component.
 */
export function AnnouncementModal({
  id,
  eligible,
  title,
  description,
  body,
  primaryLabel,
  onPrimary,
  secondary,
}: AnnouncementModalProps) {
  const dismissKey = announcementDismissKey(id);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Already dismissed once — never show again.
    if (localStorage.getItem(dismissKey) !== null) return;
    // Audience gate closed — nothing to announce for this user.
    if (!eligible) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOpen(true);
  }, [dismissKey, eligible]);

  function dismiss() {
    localStorage.setItem(dismissKey, '1');
    setOpen(false);
  }

  function handlePrimary() {
    dismiss();
    onPrimary?.();
  }

  function handleSecondary() {
    dismiss();
    secondary?.onClick();
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? dismiss() : null)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {body}
        <DialogFooter className="gap-2">
          {secondary && (
            <Button variant="ghost" onClick={handleSecondary}>
              {secondary.label}
            </Button>
          )}
          <Button onClick={handlePrimary}>{primaryLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
